import {
    ActionIcon,
    Button,
    Center,
    Divider,
    Group,
    Loader,
    Paper,
    SegmentedControl,
    Select,
    Stack,
    Text,
    TextInput,
    Title,
    Tooltip,
} from '@mantine/core'
import {type DatesRangeValue} from '@mantine/dates'
import {IconSearch, IconTruckDelivery, IconX} from '@tabler/icons-react'
import {Link} from '@tanstack/react-router'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {formatBusinessTimestamp} from 'src/api/business-time'
import {FilterMultiSelectButton} from 'src/ui/list-filter-controls'

import {type MineOrAll, useMineOrAll} from '../lib/use-mine-or-all'
import {NEW_WIZARD_SEARCH} from './create-job-storage'
import {type CustomerDataLayer, customerDataLayer} from './customer-data-layer'
import {type JobDataLayer, jobDataLayer} from './job-data-layer'
import {JobListCard} from './job-list-card'
import {JobsCustomerFilter} from './jobs-customer-filter'
import {JobsDateFilter} from './jobs-date-filter'
import {type JobSortField, type JobSortState} from './jobs-search'
import type {JobLifecycleStatus, JobListItem, JobListSortBy, JobListSortDir} from './types'
import {useJobsListFilters} from './use-jobs-list-filters'

type JobsPageProps = {
    dataLayer?: JobDataLayer
    customerDataLayer?: CustomerDataLayer
    embedded?: boolean
    syncUrlSearch?: boolean
}

const SERVER_SORTED_FIELDS = new Set<JobSortField>(['startDate', 'createdAt'])

const SORT_OPTIONS: Array<{value: string; label: string; state: JobSortState}> = [
    {
        value: 'createdAt-desc',
        label: 'Created (newest first)',
        state: {field: 'createdAt', direction: 'desc'},
    },
    {
        value: 'createdAt-asc',
        label: 'Created (oldest first)',
        state: {field: 'createdAt', direction: 'asc'},
    },
    {
        value: 'startDate-asc',
        label: 'Start date (earliest first)',
        state: {field: 'startDate', direction: 'asc'},
    },
    {
        value: 'startDate-desc',
        label: 'Start date (latest first)',
        state: {field: 'startDate', direction: 'desc'},
    },
    {value: 'name-asc', label: 'Name (A-Z)', state: {field: 'name', direction: 'asc'}},
    {value: 'name-desc', label: 'Name (Z-A)', state: {field: 'name', direction: 'desc'}},
    {
        value: 'customerName-asc',
        label: 'Customer (A-Z)',
        state: {field: 'customerName', direction: 'asc'},
    },
    {
        value: 'customerName-desc',
        label: 'Customer (Z-A)',
        state: {field: 'customerName', direction: 'desc'},
    },
    {
        value: 'serviceTypeName-asc',
        label: 'Category (A-Z)',
        state: {field: 'serviceTypeName', direction: 'asc'},
    },
    {
        value: 'serviceTypeName-desc',
        label: 'Category (Z-A)',
        state: {field: 'serviceTypeName', direction: 'desc'},
    },
    {value: 'status-asc', label: 'Status (A-Z)', state: {field: 'status', direction: 'asc'}},
    {value: 'status-desc', label: 'Status (Z-A)', state: {field: 'status', direction: 'desc'}},
]

function formatCreatedAt(createdAt: string) {
    return formatBusinessTimestamp(createdAt, 'MMM d, yyyy')
}

function dateRangeIsActive(range: DatesRangeValue): boolean {
    return range[0] != null || range[1] != null
}

const STATUS_FILTER_OPTIONS: Array<{value: JobLifecycleStatus; label: string}> = [
    {value: 'estimate', label: 'Estimate'},
    {value: 'in_progress', label: 'Active'},
    {value: 'closed', label: 'Closed'},
    {value: 'canceled', label: 'Canceled'},
]

function hasActiveFilters(
    name: string,
    customerId: string | null,
    serviceTypeName: string,
    scheduleDateRange: DatesRangeValue,
    statusFilter: JobLifecycleStatus[],
) {
    return Boolean(
        name.trim() ||
        customerId ||
        serviceTypeName.trim() ||
        dateRangeIsActive(scheduleDateRange) ||
        statusFilter.length > 0,
    )
}

function compareJobStatus(left: JobLifecycleStatus, right: JobLifecycleStatus) {
    const order: Record<JobLifecycleStatus, number> = {
        estimate: 0,
        in_progress: 1,
        closed: 2,
        canceled: 3,
    }
    return order[left] - order[right]
}

function compareJobListItems(left: JobListItem, right: JobListItem, field: JobSortField) {
    if (field === 'status') {
        return compareJobStatus(left.status, right.status)
    }

    const leftValue = String(left[field])
    const rightValue = String(right[field])
    return leftValue.localeCompare(rightValue)
}

function sortJobListItems(jobs: JobListItem[], sortState: JobSortState) {
    const directionMultiplier = sortState.direction === 'asc' ? 1 : -1
    return [...jobs].sort(
        (left, right) => directionMultiplier * compareJobListItems(left, right, sortState.field),
    )
}

function listSortBy(field: JobSortField): JobListSortBy | undefined {
    if (field === 'startDate') {
        return 'start_date'
    }
    if (field === 'createdAt') {
        return 'created_at'
    }
    return undefined
}

function listSortDir(direction: JobSortState['direction']): JobListSortDir {
    return direction === 'asc' ? 'asc' : 'desc'
}

function sortOptionValue(sortState: JobSortState) {
    return `${sortState.field}-${sortState.direction}`
}

function filterClearButton(value: string, onClear: () => void) {
    if (!value) {
        return null
    }

    return (
        <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={onClear}>
            <IconX size={14} />
        </ActionIcon>
    )
}

export function JobsPage({
    dataLayer = jobDataLayer,
    customerDataLayer: customerDataLayerProp = customerDataLayer,
    embedded = false,
    syncUrlSearch = true,
}: JobsPageProps) {
    const {
        nameFilter,
        setNameFilter,
        customerIdFilter,
        setCustomerIdFilter,
        serviceTypeNameFilter,
        setServiceTypeNameFilter,
        scheduleDateRange,
        setScheduleDateRange,
        statusFilter,
        setStatusFilter,
        sortState,
        setSortState,
        debouncedName,
        debouncedServiceTypeName,
        scheduleDateFromFilter,
        scheduleDateToFilter,
    } = useJobsListFilters(syncUrlSearch)
    const [scope, setScope] = useMineOrAll('jobs')
    const mine = !embedded && scope === 'mine'
    const [jobs, setJobs] = useState<JobListItem[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const loadJobsRequestId = useRef(0)

    const sortBy = listSortBy(sortState.field)
    const sortDir = sortBy ? listSortDir(sortState.direction) : undefined

    const loadJobs = useCallback(async () => {
        const requestId = ++loadJobsRequestId.current
        setIsLoading(true)

        try {
            const results = await dataLayer.listJobs({
                name: debouncedName,
                customerId: customerIdFilter ?? undefined,
                serviceTypeName: debouncedServiceTypeName,
                startDateFrom: scheduleDateFromFilter,
                startDateTo: scheduleDateToFilter,
                status: statusFilter.length > 0 ? statusFilter : undefined,
                sortBy,
                sortDir,
                ...(mine ? {mine: true} : {}),
            })

            if (requestId === loadJobsRequestId.current) {
                setJobs(results)
            }
        } finally {
            if (requestId === loadJobsRequestId.current) {
                setIsLoading(false)
            }
        }
    }, [
        dataLayer,
        debouncedName,
        customerIdFilter,
        debouncedServiceTypeName,
        scheduleDateFromFilter,
        scheduleDateToFilter,
        statusFilter,
        sortBy,
        sortDir,
        mine,
    ])

    useEffect(() => {
        void loadJobs()
    }, [loadJobs])

    const filtersActive = hasActiveFilters(
        debouncedName,
        customerIdFilter,
        debouncedServiceTypeName,
        scheduleDateRange,
        statusFilter,
    )

    const sortedJobs = useMemo(() => {
        if (SERVER_SORTED_FIELDS.has(sortState.field)) {
            return jobs
        }
        return sortJobListItems(jobs, sortState)
    }, [jobs, sortState])

    return (
        <>
            {!embedded ? (
                <Group justify="space-between" mb="md">
                    <Title order={1}>Jobs</Title>
                    <Group>
                        <Button component={Link} to="/projects" size="sm" variant="outline">
                            View all orders
                        </Button>
                        <Tooltip
                            label="A job is a single piece of work, like moving equipment to a site."
                            withArrow
                        >
                            <Button
                                component={Link}
                                to="/jobs/create"
                                search={NEW_WIZARD_SEARCH}
                                leftSection={<IconTruckDelivery size={18} stroke={1.5} />}
                            >
                                Create job
                            </Button>
                        </Tooltip>
                    </Group>
                </Group>
            ) : null}

            <Stack gap="md">
                <Paper withBorder p="sm" radius="md">
                    <Group gap="xs" wrap="wrap" align="center">
                        {!embedded ? (
                            <SegmentedControl
                                aria-label="Whose jobs"
                                size="sm"
                                value={scope}
                                onChange={(value) => setScope(value as MineOrAll)}
                                data={[
                                    {value: 'mine', label: 'Mine'},
                                    {value: 'all', label: 'All'},
                                ]}
                            />
                        ) : null}
                        <TextInput
                            aria-label="Job name"
                            placeholder="Search job names or numbers..."
                            leftSection={<IconSearch size={16} stroke={1.5} />}
                            rightSection={filterClearButton(nameFilter, () => setNameFilter(''))}
                            value={nameFilter}
                            onChange={(event) => setNameFilter(event.currentTarget.value)}
                            style={{flex: '1 1 150px', minWidth: 140}}
                        />
                        <FilterMultiSelectButton
                            label="Status"
                            description="Show jobs in any of the selected lifecycle statuses"
                            options={STATUS_FILTER_OPTIONS}
                            value={statusFilter}
                            primaryValue="in_progress"
                            onChange={setStatusFilter}
                        />
                        <JobsCustomerFilter
                            value={customerIdFilter}
                            onChange={setCustomerIdFilter}
                            dataLayer={customerDataLayerProp}
                        />
                        <JobsDateFilter value={scheduleDateRange} onChange={setScheduleDateRange} />
                        <TextInput
                            aria-label="Category"
                            placeholder="Category"
                            rightSection={filterClearButton(serviceTypeNameFilter, () =>
                                setServiceTypeNameFilter(''),
                            )}
                            value={serviceTypeNameFilter}
                            onChange={(event) =>
                                setServiceTypeNameFilter(event.currentTarget.value)
                            }
                            w={140}
                        />
                        <Divider orientation="vertical" visibleFrom="sm" />
                        <Select
                            aria-label="Sort by"
                            data={SORT_OPTIONS.map((option) => ({
                                value: option.value,
                                label: option.label,
                            }))}
                            value={sortOptionValue(sortState)}
                            onChange={(value) => {
                                const option = SORT_OPTIONS.find((entry) => entry.value === value)
                                if (option) {
                                    setSortState(option.state)
                                }
                            }}
                            w={190}
                            allowDeselect={false}
                        />
                    </Group>
                </Paper>

                <Group justify="space-between" align="center">
                    <Group gap="xs" align="center">
                        <Text size="sm" c="dimmed">
                            {sortedJobs.length === 1 ? '1 job' : `${sortedJobs.length} jobs`}
                        </Text>
                        {filtersActive && (
                            <Button
                                variant="subtle"
                                size="compact-sm"
                                onClick={() => {
                                    setNameFilter('')
                                    setCustomerIdFilter(null)
                                    setServiceTypeNameFilter('')
                                    setScheduleDateRange([null, null])
                                    setStatusFilter([])
                                }}
                            >
                                Clear all filters
                            </Button>
                        )}
                        {isLoading && jobs.length > 0 ? (
                            <Group gap={6} align="center" aria-live="polite">
                                <Loader size="xs" aria-label="Updating jobs" />
                                <Text size="sm" c="dimmed">
                                    Updating…
                                </Text>
                            </Group>
                        ) : null}
                    </Group>
                </Group>

                {isLoading && jobs.length === 0 ? (
                    <Center mih={180}>
                        <Loader />
                    </Center>
                ) : sortedJobs.length === 0 ? (
                    <Center mih={180}>
                        <Text c="dimmed">
                            {filtersActive ? 'No jobs match your filters.' : 'No jobs yet.'}
                        </Text>
                    </Center>
                ) : (
                    <Stack gap="sm">
                        {sortedJobs.map((job) => (
                            <JobListCard
                                key={job.id}
                                job={job}
                                createdAtLabel={formatCreatedAt(job.createdAt)}
                            />
                        ))}
                    </Stack>
                )}
            </Stack>
        </>
    )
}
